import { useState } from 'react';

export default function AideDomicileCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', frequency: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));
    try {
      await fetch('https://formsubmit.co/ajax/info@casarenovaplus.ch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } finally {
      setSubmitted(true);
    }
  };

  return (
    <section id="contact-aide" className="py-24 bg-gray-950">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <h2 className="text-4xl font-bold text-white mb-6">Besoin d'un accompagnement ?</h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-10">
              Contactez-nous pour discuter des besoins de votre proche. Nous vous proposons une évaluation gratuite à domicile et un plan d'accompagnement personnalisé.
            </p>
            <div className="space-y-6 mb-10">
              {[
                { icon: 'ri-phone-line', title: 'Téléphone', value: '+41 76 480 31 88 | +41 76 565 42 03', sub: 'Disponible 7j/7' },
                { icon: 'ri-mail-line', title: 'Email', value: 'info@casarenovaplus.ch', sub: 'Réponse sous 24h' },
                { icon: 'ri-map-pin-line', title: 'Zone d\'intervention', value: 'Neuenegg et Suisse Romande', sub: 'Austrasse 95, 3176 Neuenegg' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-lg shrink-0">
                    <i className={`${item.icon} text-lg text-orange-400`}></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">{item.title}</p>
                    <p className="text-white font-semibold">{item.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-orange-500/10 rounded-xl p-6 border border-orange-500/20">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-2xl text-orange-400 flex-shrink-0 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-white mb-2">Évaluation gratuite à domicile</h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Nous nous déplaçons gratuitement pour rencontrer votre proche et évaluer ses besoins. Aucun engagement, devis transparent et personnalisé.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-500/20 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-orange-400"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Demande envoyée !</h3>
                <p className="text-gray-400">Nous vous recontactons sous 24h pour organiser une évaluation gratuite.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <h3 className="text-xl font-bold text-white mb-6">Votre demande d'accompagnement</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Nom complet *</label>
                    <input type="text" name="name" required onChange={handleChange} placeholder="Jean Dupont"
                      className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Téléphone *</label>
                    <input type="tel" name="phone" required onChange={handleChange} placeholder="+41 79 000 00 00"
                      className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                  <input type="email" name="email" required onChange={handleChange} placeholder="votre@email.ch"
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Type d'aide souhaité *</label>
                  <select name="service" required onChange={handleChange}
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer">
                    <option value="">Sélectionnez</option>
                    <option value="Aide ménagère">Aide ménagère</option>
                    <option value="Courses et commissions">Courses et commissions</option>
                    <option value="Accompagnement et présence">Accompagnement et présence</option>
                    <option value="Aide aux repas">Aide aux repas</option>
                    <option value="Plusieurs services">Plusieurs services</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Fréquence souhaitée</label>
                  <select name="frequency" onChange={handleChange}
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer">
                    <option value="">Sélectionnez</option>
                    <option value="Quotidien">Quotidien</option>
                    <option value="Plusieurs fois par semaine">Plusieurs fois par semaine</option>
                    <option value="Hebdomadaire">Hebdomadaire</option>
                    <option value="Ponctuel">Ponctuel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Message / Précisions</label>
                  <textarea name="message" rows={3} maxLength={500} onChange={handleChange}
                    placeholder="Décrivez la situation et les besoins de votre proche..."
                    className="w-full rounded-lg bg-gray-800 border border-gray-700 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"></textarea>
                </div>
                <button type="submit" className="w-full rounded-full bg-orange-500 px-6 py-3 text-sm font-semibold text-white hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer">
                  Envoyer ma demande
                </button>
                <p className="text-xs text-gray-500 text-center">Évaluation gratuite à domicile. Réponse sous 24h. Sans engagement.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
